import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import AdminLayout from "@/components/AdminLayout";
import { toast } from "sonner";
import { Loader2, Phone, Mail, ArrowLeft, Save, Bike, ShoppingBag, ClipboardList, StickyNote, Wallet, Calendar, MapPin, Pencil, X, Sparkles, Ban } from "lucide-react";
import { FlagCustomerCard } from "@/components/FlagCustomerCard";
import { CustomerLoyaltyCard } from "@/components/CustomerLoyaltyCard";

const tenureFrom = (firstSeenIso) => {
  if (!firstSeenIso) return null;
  const first = new Date(firstSeenIso);
  if (Number.isNaN(first.getTime())) return null;
  const now = new Date();
  const years = (now - first) / (1000 * 60 * 60 * 24 * 365.25);
  const year = first.getFullYear();
  let tier;
  if (years >= 3) tier = { key: "old_timer", label: "Long-Time Rider" };
  else if (years >= 1) tier = { key: "cruiser", label: "Regular Cruiser" };
  else tier = { key: "new_rider", label: "New Rider" };
  return { year, years: Math.round(years * 10) / 10, tier };
};

const TENURE_STYLES = {
  old_timer: "bg-spark-yellow text-spark-ink border-spark-yellow",
  cruiser: "bg-spark-teal/15 text-spark-teal-dark border-spark-teal/30",
  returner: "bg-spark-teal/15 text-spark-teal-dark border-spark-teal/30",
  new_rider: "bg-spark-floor text-spark-slate border-spark-line",
};

const StatusPill = ({ status }) => (
  <span className="inline-block px-2 py-0.5 rounded-sm bg-spark-floor text-spark-slate text-[10px] uppercase tracking-wider font-bold">{status || "—"}</span>
);

const PaymentPill = ({ status }) => {
  const cls = status === "Paid" ? "bg-emerald-100 text-emerald-700" : status === "Partial" ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-600";
  return <span className={`inline-block px-2 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-bold ${cls}`}>{status || "Unpaid"}</span>;
};

export default function AdminCustomerDetail() {
  const { email } = useParams();
  const navigate = useNavigate();
  const decoded = decodeURIComponent(email);
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [editOpen, setEditOpen] = useState(false);

  useEffect(() => {
    let dead = false;
    api.get(`/admin/customers/${encodeURIComponent(decoded)}`).then((res) => {
      if (dead) return;
      setC(res.data);
      setNotes(res.data?.notes || "");
    }).catch(() => toast.error("Couldn't load customer")).finally(() => !dead && setLoading(false));
    return () => { dead = true; };
  }, [decoded]);

  const saveNotes = async () => {
    setSavingNotes(true);
    try {
      const res = await api.patch(`/admin/customers/${encodeURIComponent(decoded)}/notes`, { notes });
      setC(res.data);
      toast.success("Notes saved");
    } catch (e) {
      toast.error("Couldn't save notes");
    } finally {
      setSavingNotes(false);
    }
  };

  if (loading) return <AdminLayout title="Customer" eyebrow="CRM"><div className="flex justify-center py-20"><Loader2 className="size-6 animate-spin text-spark-yellow" /></div></AdminLayout>;
  if (!c) return <AdminLayout title="Customer" eyebrow="CRM"><p className="text-center text-spark-slate py-10">Customer not found.</p></AdminLayout>;

  return (
    <AdminLayout title={c.name || c.email} eyebrow="Customer">
      <div className="mb-4">
        <Link to="/admin/customers" className="inline-flex items-center gap-2 text-sm text-spark-slate hover:text-spark-teal" data-testid="customer-back-link">
          <ArrowLeft className="size-4" /> Back to customers
        </Link>
      </div>

      <div className="grid lg:grid-cols-12 gap-6" data-testid="admin-customer-detail">
        {/* Left: profile + stats + history */}
        <div className="lg:col-span-8 space-y-6 min-w-0">
          <div className="surface-card p-5 sm:p-6">
            <div className="flex items-start gap-4 flex-wrap">
              <div className="size-14 rounded-full bg-spark-teal/15 text-spark-teal flex items-center justify-center font-bold uppercase text-xl shrink-0">
                {(c.name || c.email).slice(0, 1).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="label-mono text-spark-teal">Customer</p>
                <h1 className="font-display uppercase tracking-tight text-2xl sm:text-3xl font-bold text-spark-ink break-words">{c.name || "(no name on file)"}</h1>
                {c.do_not_service && (
                  <span className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm bg-red-600 text-white text-[10px] uppercase tracking-wider font-bold" data-testid="customer-flagged-badge">
                    <Ban className="size-3" /> Do not service
                  </span>
                )}
                {(() => {
                  const t = tenureFrom(c.first_seen);
                  if (!t) return null;
                  return (
                    <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="customer-since-badge">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-[10px] uppercase tracking-wider font-bold ${TENURE_STYLES[t.tier.key] || TENURE_STYLES.new_rider}`}>
                        <Sparkles className="size-3" /> Customer since {t.year}
                      </span>
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-[10px] uppercase tracking-wider font-bold ${TENURE_STYLES[t.tier.key] || TENURE_STYLES.new_rider}`} data-testid="customer-tier-badge">
                        <Bike className="size-3" /> {t.tier.label}
                      </span>
                    </div>
                  );
                })()}
                <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-spark-slate">
                  {c.phone && <a href={`tel:${c.phone}`} className="inline-flex items-center gap-1.5 hover:text-spark-teal"><Phone className="size-3.5" /> {c.phone}</a>}
                  <a href={`mailto:${c.email}`} className="inline-flex items-center gap-1.5 hover:text-spark-teal min-w-0"><Mail className="size-3.5 shrink-0" /> <span className="truncate">{c.email}</span></a>
                  {c.default_address && <span className="inline-flex items-center gap-1.5"><MapPin className="size-3.5 shrink-0" /> <span className="truncate">{c.default_address}</span></span>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setEditOpen(true)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-spark-line text-spark-slate hover:text-spark-teal hover:border-spark-teal text-xs uppercase tracking-wider font-bold shrink-0"
                data-testid="edit-customer-btn"
                title="Edit customer details"
              >
                <Pencil className="size-3.5" /> Edit
              </button>
            </div>
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Mini label="Jobs" value={c.jobs_count} icon={ClipboardList} />
              <Mini label="Orders" value={c.orders_count} icon={ShoppingBag} />
              <Mini label="Total spend" value={`$${(c.total_spend || 0).toFixed(2)}`} icon={Wallet} />
              <Mini label="First seen" value={c.first_seen ? new Date(c.first_seen).toLocaleDateString("en-NZ", { dateStyle: "medium" }) : "—"} icon={Calendar} />
            </div>
            {c.scooters?.length > 0 && (
              <div className="mt-5">
                <p className="label-mono mb-2 flex items-center gap-1.5"><Bike className="size-3.5" /> Scooters seen</p>
                <div className="flex flex-wrap gap-2">
                  {c.scooters.map((s, i) => (
                    <span key={i} className="px-2.5 py-1 rounded-sm bg-spark-floor text-spark-ink text-xs font-bold uppercase tracking-wider">{s.brand} {s.model}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Job history */}
          <div className="surface-card p-5 sm:p-6">
            <h2 className="font-display uppercase tracking-tight text-lg font-bold text-spark-ink mb-1 flex items-center gap-2"><ClipboardList className="size-5 text-spark-teal" /> Job history <span className="text-spark-slate text-sm font-normal">· {c.jobs_count}</span></h2>
            {c.jobs.length === 0 ? <p className="text-sm text-spark-slate py-4">No repair jobs yet.</p> : (
              <ul className="divide-y divide-spark-line" data-testid="customer-jobs-list">
                {c.jobs.map((j) => (
                  <li key={j.id}>
                    <Link to={`/admin/jobs/${j.id}`} className="flex items-center gap-3 py-3 hover:bg-spark-floor -mx-5 px-5 transition rounded-sm" data-testid={`customer-job-${j.ref_number}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold text-spark-teal-dark text-sm">{j.ref_number}</span>
                          <StatusPill status={j.status} />
                          <PaymentPill status={j.payment_status} />
                        </div>
                        <p className="text-sm text-spark-ink truncate">{j.scooter_brand} {j.scooter_model} · {j.issue_description}</p>
                        <p className="text-[11px] text-spark-slate">{new Date(j.created_at).toLocaleDateString("en-NZ", { dateStyle: "medium" })}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-display uppercase tracking-tight font-bold text-spark-ink">{j.final_price != null ? `$${Number(j.final_price).toFixed(2)}` : j.quoted_total ? `~$${Number(j.quoted_total).toFixed(2)}` : "—"}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* Order history */}
          <div className="surface-card p-5 sm:p-6">
            <h2 className="font-display uppercase tracking-tight text-lg font-bold text-spark-ink mb-1 flex items-center gap-2"><ShoppingBag className="size-5 text-spark-teal" /> Shop orders <span className="text-spark-slate text-sm font-normal">· {c.orders_count}</span></h2>
            {c.orders.length === 0 ? <p className="text-sm text-spark-slate py-4">No shop orders yet.</p> : (
              <ul className="divide-y divide-spark-line" data-testid="customer-orders-list">
                {c.orders.map((o) => (
                  <li key={o.id}>
                    <Link to={`/admin/orders/${o.id}`} className="flex items-center gap-3 py-3 hover:bg-spark-floor -mx-5 px-5 transition rounded-sm" data-testid={`customer-order-${o.order_number}`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono font-bold text-spark-teal-dark text-sm">{o.order_number}</span>
                          <StatusPill status={o.status} />
                        </div>
                        <p className="text-xs text-spark-slate truncate">{(o.items || []).length} item{(o.items || []).length === 1 ? "" : "s"}</p>
                        <p className="text-[11px] text-spark-slate">{new Date(o.created_at).toLocaleDateString("en-NZ", { dateStyle: "medium" })}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="font-display uppercase tracking-tight font-bold text-spark-ink">${Number(o.total || 0).toFixed(2)}</p>
                      </div>
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        {/* Right: notes */}
        <aside className="lg:col-span-4 lg:sticky lg:top-24 self-start space-y-6 min-w-0">
          <CustomerLoyaltyCard customer={c} onSaved={setC} />
          <FlagCustomerCard key={`${c.email}-${c.do_not_service}-${c.flagged_at}`} customer={c} onSaved={setC} />
          <div className="surface-card p-5 sm:p-6" data-testid="customer-notes-card">
            <div className="flex items-center gap-2 mb-2">
              <StickyNote className="size-4 text-spark-teal" />
              <p className="label-mono text-spark-teal">Internal notes</p>
            </div>
            <p className="text-xs text-spark-slate mb-3">Visible to admin only. Use for preferences, contact tips, history not captured in jobs.</p>
            <textarea
              rows={6}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="input-style resize-none text-sm"
              placeholder="e.g. Prefers text messages. Has a Pure Air and a Mearth — usually rides daily to work."
              data-testid="customer-notes"
            />
            <button onClick={saveNotes} disabled={savingNotes} className="btn-primary w-full mt-3" data-testid="save-customer-notes">
              {savingNotes ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Save notes
            </button>
          </div>
        </aside>
      </div>
      {editOpen && (
        <EditCustomerDialog
          customer={c}
          onClose={() => setEditOpen(false)}
          onSaved={(next) => {
            setEditOpen(false);
            if (next.email && next.email !== c.email) {
              // Email changed — the URL is keyed off email, so redirect.
              navigate(`/admin/customers/${encodeURIComponent(next.email)}`, { replace: true });
            } else {
              setC(next);
              toast.success("Customer updated");
            }
          }}
        />
      )}
    </AdminLayout>
  );
}


const EditCustomerDialog = ({ customer, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name: customer.name || "",
    phone: customer.phone || "",
    email: customer.email || "",
    default_address: customer.default_address || "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const emailChanged = form.email.trim().toLowerCase() !== (customer.email || "").toLowerCase();

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email.trim()) { toast.error("Email is required"); return; }
    // Confirm destructive email change
    if (emailChanged && !window.confirm(
      `You're changing this customer's email from\n\n${customer.email}\n\nto\n\n${form.email.trim().toLowerCase()}\n\n` +
      `Every past job, order and review will move to the new email. Continue?`
    )) return;
    setSaving(true);
    try {
      const res = await api.patch(
        `/admin/customers/${encodeURIComponent(customer.email)}`,
        {
          name: form.name.trim(),
          phone: form.phone.trim(),
          default_address: form.default_address.trim(),
          ...(emailChanged ? { email: form.email.trim().toLowerCase() } : {}),
        },
      );
      onSaved(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" role="dialog" aria-modal="true" data-testid="edit-customer-modal">
      <div className="w-full max-w-md rounded-xl border border-spark-line bg-white shadow-2xl overflow-hidden">
        <div className="bg-spark-teal-deep px-6 py-4 flex items-center justify-between gap-3">
          <div>
            <p className="label-mono text-spark-yellow">Edit customer</p>
            <h2 className="font-display uppercase tracking-tight text-lg font-bold text-white truncate max-w-[280px]">{customer.name || customer.email}</h2>
          </div>
          <button onClick={onClose} className="text-slate-300 hover:text-white p-1" data-testid="edit-customer-close"><X className="size-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4 text-sm">
          <div>
            <label className="label-mono mb-1.5 inline-block">Full name</label>
            <input type="text" value={form.name} onChange={(e) => set("name", e.target.value)} className="input-style" data-testid="edit-customer-name" />
          </div>
          <div>
            <label className="label-mono mb-1.5 inline-block">Phone</label>
            <input type="tel" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="input-style" data-testid="edit-customer-phone" />
          </div>
          <div>
            <label className="label-mono mb-1.5 inline-block">Email</label>
            <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className="input-style" data-testid="edit-customer-email" />
            {emailChanged && (
              <p className="text-[11px] text-amber-700 mt-1.5 leading-snug">
                <strong>Heads up:</strong> Changing the email will move every past job, order and review from <code className="bg-amber-100 px-1 rounded">{customer.email}</code> to the new address.
              </p>
            )}
          </div>
          <div>
            <label className="label-mono mb-1.5 inline-block">Default address (optional)</label>
            <input type="text" value={form.default_address} onChange={(e) => set("default_address", e.target.value)} placeholder="e.g. 12 Bartholomew Rd, Levin" className="input-style" data-testid="edit-customer-address" />
            <p className="text-[11px] text-spark-slate mt-1">Kept against the profile for quick pickup / drop-off pre-fill on new jobs.</p>
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 rounded-md text-xs uppercase tracking-wider font-semibold text-spark-slate hover:bg-spark-floor" data-testid="edit-customer-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="btn-primary text-xs" data-testid="edit-customer-save">
              {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />} Save changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Mini = ({ icon: Icon, label, value }) => (
  <div className="bg-spark-floor rounded-sm p-2.5">
    <div className="flex items-center gap-1.5 mb-0.5"><Icon className="size-3 text-spark-teal" /><p className="text-[10px] uppercase tracking-[0.2em] font-bold text-spark-slate">{label}</p></div>
    <p className="font-display uppercase tracking-tight font-bold text-spark-ink truncate">{value}</p>
  </div>
);
